import * as Context from "effect/Context";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import type * as Scope from "effect/Scope";

import type { HostEnvironmentEntry } from "./host.ts";
import { ConnectionBlockedError } from "./model.ts";
import type {
  ConnectionAttemptError,
  ConnectionTarget,
  PreparedConnection,
  RemoteConnectionTarget,
  SshConnectionTarget,
} from "./model.ts";
import {
  EnvironmentWebSocketAuthorization,
  environmentRouteSocketUrl,
  environmentSocketUrl,
} from "./webSocketAuthorization.ts";
import { CloudWorkspaceConnectionGateway } from "./cloudWorkspaceConnection.ts";
import { SshEnvironmentGateway } from "./sshEnvironment.ts";

export class ConnectionResolver extends Context.Service<
  ConnectionResolver,
  {
    readonly prepare: (
      entry: HostEnvironmentEntry,
    ) => Effect.Effect<PreparedConnection, ConnectionAttemptError, Scope.Scope>;
  }
>()("@afar-research/client-runtime/connection/resolver/ConnectionResolver") {}

export const make = Effect.gen(function* () {
  const authorization = yield* EnvironmentWebSocketAuthorization;
  const cloudWorkspaces = yield* CloudWorkspaceConnectionGateway;
  const ssh = yield* SshEnvironmentGateway;

  /**
   * A forward is opened for every attempt rather than assumed.
   *
   * The stored target is the destination, not an address: the loopback port an SSH Environment
   * answers on exists only while this client holds the forward open, so the base URLs come back
   * from the gateway each time. The Environment identity is checked there, because an alias the
   * user repointed now names a different machine.
   */
  const reachSsh = Effect.fn("clientRuntime.host.reachSsh")(function* (
    target: SshConnectionTarget,
  ) {
    return yield* ssh.prepare({
      expectedEnvironmentId: target.environmentId,
      target: target.ssh,
    });
  });

  /**
   * A Cloud Workspace answers at its own hostname, which AFAR Connect names when it brokers the
   * introduction. The saved base URLs are replaced rather than read, because the hostname
   * belongs to the workspace's current hosting rather than to this client's record of it.
   */
  const reachRemote = Effect.fn("clientRuntime.host.reachRemote")(function* (
    target: RemoteConnectionTarget,
  ) {
    return yield* cloudWorkspaces.open({
      environmentId: target.environmentId,
      label: target.label,
    });
  });

  /** Another socket on the same Environment, authorized by a fresh ticket from `bearer`. */
  const routeSocket =
    (
      input: { readonly httpBaseUrl: string; readonly wsBaseUrl: string; readonly label: string },
      bearer: string | null,
    ) =>
    (path: string) =>
      bearer === null
        ? Effect.succeed(environmentRouteSocketUrl(input.wsBaseUrl, path))
        : authorization
            .mintTicket({ httpBaseUrl: input.httpBaseUrl, bearer, label: input.label })
            .pipe(Effect.map((ticket) => environmentRouteSocketUrl(input.wsBaseUrl, path, ticket)));

  const prepare = Effect.fn("clientRuntime.host.prepare")(function* (entry: HostEnvironmentEntry) {
    const target: ConnectionTarget = entry.target;
    yield* Effect.annotateCurrentSpan({
      "environment.id": target.environmentId,
      "environment.target.kind": target._tag,
      ...(target._tag === "RemoteConnectionTarget"
        ? { "environment.reachability": "cloud-workspace-connection" }
        : {}),
      ...(target._tag === "SshConnectionTarget"
        ? { "environment.reachability": "ssh-forward" }
        : {}),
      ...(target._tag === "PairedConnectionTarget" ? { "environment.reachability": "paired" } : {}),
    });
    if (target._tag === "PairedConnectionTarget") {
      // Paired by link: the address is the one the link named, and the session the destination
      // issued is the whole authority. A different Environment answering there would not know it.
      return {
        environmentId: target.environmentId,
        label: target.label,
        httpBaseUrl: target.httpBaseUrl,
        socketUrl: environmentSocketUrl(
          target.wsBaseUrl,
          yield* authorization.mintTicket({
            httpBaseUrl: target.httpBaseUrl,
            bearer: target.session,
            label: target.label,
          }),
        ),
        target,
        authorizeSocket: routeSocket(target, target.session),
      } satisfies PreparedConnection;
    }
    if (target._tag === "SshConnectionTarget") {
      const endpoint = yield* reachSsh(target);
      return {
        environmentId: endpoint.environmentId,
        label: target.label,
        httpBaseUrl: endpoint.httpBaseUrl,
        socketUrl: environmentSocketUrl(
          endpoint.wsBaseUrl,
          yield* authorization.mintTicket({
            httpBaseUrl: endpoint.httpBaseUrl,
            bearer: endpoint.session,
            label: target.label,
          }),
        ),
        target,
        authorizeSocket: routeSocket({ ...endpoint, label: target.label }, endpoint.session),
      } satisfies PreparedConnection;
    }
    if (target._tag !== "RemoteConnectionTarget") {
      // A browser's cookie rides the upgrade on its own; a bearer is exchanged for a ticket.
      const mint = (bearer: string) =>
        authorization.mintTicket({ httpBaseUrl: target.httpBaseUrl, bearer, label: target.label });
      const bearer = yield* authorization.primaryBearer(target);
      const ticket =
        bearer === null
          ? undefined
          : yield* mint(bearer).pipe(
              // A refused bearer is one the platform can replace — its Environment restarted on a
              // fresh Host Store, say — so it gets one chance to pair again before this blocks.
              Effect.catchIf(
                (error) => error._tag === "ConnectionBlockedError" && error.reason === "credential",
                (error) =>
                  authorization
                    .primaryBearer(target, { refresh: true })
                    .pipe(
                      Effect.flatMap((fresh) =>
                        fresh === null || fresh === bearer ? Effect.fail(error) : mint(fresh),
                      ),
                    ),
              ),
            );
      return {
        environmentId: target.environmentId,
        label: target.label,
        httpBaseUrl: target.httpBaseUrl,
        socketUrl: environmentSocketUrl(target.wsBaseUrl, ticket),
        target,
        authorizeSocket: routeSocket(target, bearer),
      } satisfies PreparedConnection;
    }
    // Reachability first: a credential minted against an Environment that cannot be reached would
    // be spent before the first request, and the introduction is what the retry ladder times.
    const endpoint = yield* reachRemote(target);
    if (endpoint.session === undefined) {
      // Reachability without authorization. The forward is open and the Environment issued
      // nothing, so there is no credential to connect with and pretending otherwise would fail
      // later and less clearly.
      return yield* new ConnectionBlockedError({
        reason: "credential",
        detail: `${target.label} did not issue Environment Access over its Cloud Workspace Connection.`,
      });
    }
    const ticket = yield* authorization.mintTicket({
      httpBaseUrl: endpoint.httpBaseUrl,
      bearer: endpoint.session,
      label: target.label,
    });
    return {
      environmentId: target.environmentId,
      label: target.label,
      httpBaseUrl: endpoint.httpBaseUrl,
      socketUrl: environmentSocketUrl(endpoint.wsBaseUrl, ticket),
      target,
      authorizeSocket: routeSocket({ ...endpoint, label: target.label }, endpoint.session),
    } satisfies PreparedConnection;
  });
  return ConnectionResolver.of({ prepare });
});

export const layer = Layer.effect(ConnectionResolver, make);
